import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  Dashboard,
  Insights,
  Refresh,
  Psychology,
  Schedule,
  MovieFilter,
  AttachMoney,
  Speed,
  CheckCircle,
  CalendarToday,
  Pages,
  TheaterComedy,
  AccountBalance,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { ChatCoPilot } from '../copilot/ChatCoPilot';
import InsightCard from '../insights/InsightCard';
import { fetchAllProductionInsights } from '../../services/productionInsightsApi';
import {
  ProductionInsightsData,
  ProductionInsight,
  ProductionStatus,
  INSIGHT_COLORS,
  CONFIDENCE_COLORS,
} from '../../types/productionInsights';

const ProductionInsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<ProductionInsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copilotPrompt, setCopilotPrompt] = useState<string>('');
  const [precomputedResponse, setPrecomputedResponse] = useState<string>('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAllProductionInsights();
      setData(response);
    } catch (err) {
      console.error('Failed to load production insights:', err);
      setError('Failed to load production insights. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExploreWithAI = (prompt: string, precomputedExplanation?: string) => {
    if (precomputedExplanation) {
      setPrecomputedResponse(precomputedExplanation);
      setCopilotPrompt('');
    } else {
      setCopilotPrompt(prompt);
      setPrecomputedResponse('');
    }
    const copilotPanel = document.getElementById('copilot-panel');
    if (copilotPanel) {
      copilotPanel.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const status: ProductionStatus = data?.status?.production_status || 'in_progress';
  const isWrapped = status === 'wrapped';

  const getPageTitle = () => {
    if (isWrapped) {
      return 'Production Recap';
    }
    return `Production Insights - Day ${data?.status?.current_day || 0} of ${data?.status?.total_scheduled_days || 0}`;
  };

  const getStatusChip = () => {
    if (isWrapped) {
      return (
        <Chip
          label="Production Complete"
          size="small"
          sx={{
            backgroundColor: '#48BB78',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      );
    }
    return (
      <Chip
        label="In Production"
        size="small"
        sx={{
          backgroundColor: '#4299E1',
          color: 'white',
          fontWeight: 600,
          fontSize: '0.7rem',
        }}
      />
    );
  };

  const insights: Array<{ key: string; insight: ProductionInsight | undefined; icon: React.ReactNode; color: string }> = [
    { key: 'workloadOvertime', insight: data?.insights?.workloadOvertime, icon: <Schedule />, color: INSIGHT_COLORS.workloadOvertime },
    { key: 'droppedScenes', insight: data?.insights?.droppedScenes, icon: <MovieFilter />, color: INSIGHT_COLORS.droppedScenes },
    { key: 'financial', insight: data?.insights?.financial, icon: <AttachMoney />, color: INSIGHT_COLORS.financial },
    { key: 'episodeEfficiency', insight: data?.insights?.episodeEfficiency, icon: <Speed />, color: INSIGHT_COLORS.episodeEfficiency },
    { key: 'scheduleAccuracy', insight: data?.insights?.scheduleAccuracy, icon: <CheckCircle />, color: INSIGHT_COLORS.scheduleAccuracy },
  ];

  const SummaryCard: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string;
    color: string;
  }> = ({ icon, label, value, color }) => (
    <Card
      sx={{
        backgroundColor: '#2D3748',
        border: '1px solid #4A5568',
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={600} color="white">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#1A202C' }}>
      <AppBar position="static" sx={{ backgroundColor: '#2D3748' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 700, color: 'white', mr: 1, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              FilmIQ
            </Typography>
            {getStatusChip()}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Dashboard">
              <IconButton onClick={() => navigate('/')} sx={{ color: 'white' }}>
                <Dashboard />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insights">
              <IconButton onClick={() => navigate('/insights')} sx={{ color: 'white' }}>
                <Insights />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={loadData} disabled={loading} sx={{ color: 'white' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              <Typography variant="h5" fontWeight={700} color="white" gutterBottom>
                {getPageTitle()}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {isWrapped
                  ? 'Comprehensive analysis of production patterns, efficiency, and insights'
                  : 'Emerging patterns and correlations from production data'}
              </Typography>

              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}>
                  <SummaryCard
                    icon={<CalendarToday />}
                    label="Days"
                    value={data?.summary?.days?.label || '—'}
                    color="#4299E1"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <SummaryCard
                    icon={<Pages />}
                    label="Pages"
                    value={data?.summary?.pages?.label || '—'}
                    color="#48BB78"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <SummaryCard
                    icon={<TheaterComedy />}
                    label="Scenes"
                    value={data?.summary?.scenes?.label || '—'}
                    color="#ED8936"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <SummaryCard
                    icon={<AccountBalance />}
                    label="Budget"
                    value={data?.summary?.budget?.label || '—'}
                    color={data?.summary?.budget?.variance && data.summary.budget.variance > 0 ? '#48BB78' : '#F56565'}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ px: 3, pb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600} color="white">
                  {isWrapped ? 'Production Insights' : 'Emerging Insights'}
                </Typography>
                <Chip
                  icon={<Psychology sx={{ color: 'white !important' }} />}
                  label={`${insights.filter(i => i.insight).length} Insights`}
                  sx={{
                    backgroundColor: '#4A5568',
                    color: 'white',
                    fontWeight: 500,
                  }}
                />
              </Box>

              <Grid container spacing={3}>
                {insights.map(({ key, insight, icon, color }) => {
                  if (!insight) return null;

                  const confidenceColor = CONFIDENCE_COLORS[insight.confidence_level || 'medium'];

                  return (
                    <Grid item xs={12} md={6} lg={4} key={key}>
                      <Paper
                        sx={{
                          backgroundColor: '#2D3748',
                          borderRadius: 2,
                          border: '1px solid #4A5568',
                          overflow: 'hidden',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            borderBottom: '1px solid #4A5568',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ color }}>{icon}</Box>
                            <Typography variant="subtitle1" fontWeight={600} color="white">
                              {insight.title}
                            </Typography>
                          </Box>
                          <Tooltip title={`Confidence: ${insight.confidence_level} (${insight.sample_size} days)`}>
                            <Chip
                              label={insight.confidence_level}
                              size="small"
                              sx={{
                                backgroundColor: `${confidenceColor}20`,
                                color: confidenceColor,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize',
                              }}
                            />
                          </Tooltip>
                        </Box>

                        <Box sx={{ p: 2, flexGrow: 1 }}>
                          <Typography
                            variant="body2"
                            color="white"
                            sx={{
                              fontWeight: 500,
                              mb: 1,
                              lineHeight: 1.5,
                            }}
                          >
                            {insight.finding}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {insight.description}
                          </Typography>

                          {!isWrapped && insight.confidence_level !== 'high' && (
                            <Chip
                              label="Emerging Pattern"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(159, 122, 234, 0.2)',
                                color: '#9F7AEA',
                                fontWeight: 500,
                                fontSize: '0.65rem',
                                mb: 1,
                              }}
                            />
                          )}
                        </Box>

                        <Box sx={{ p: 2, pt: 0 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<Psychology />}
                            onClick={() => handleExploreWithAI(insight.copilot_prompt, insight.precomputed_explanation)}
                            sx={{
                              borderColor: '#4A5568',
                              color: '#A0AEC0',
                              '&:hover': {
                                borderColor: color,
                                color: color,
                                backgroundColor: `${color}10`,
                              },
                            }}
                          >
                            Explore with AI
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {insights.filter(i => i.insight).length === 0 && (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'text.secondary',
                  }}
                >
                  <Insights sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    No Insights Available Yet
                  </Typography>
                  <Typography variant="body2">
                    Insights will appear as more production data is collected and analyzed.
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        <Box
          id="copilot-panel"
          sx={{
            height: '50vh',
            minHeight: 350,
            flexShrink: 0,
            borderTop: '1px solid #4A5568',
            backgroundColor: '#2D3748',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #4A5568' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Psychology sx={{ color: '#4299E1' }} />
              <Typography variant="subtitle1" fontWeight={600} color="white">
                Production AI Assistant
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Click "Explore with AI" on any insight to dive deeper, or ask your own questions
            </Typography>
          </Box>
          <Box sx={{ height: 'calc(100% - 72px)' }}>
            <ChatCoPilot
              initialPrompt={copilotPrompt}
              enableStarterPrompts={false}
              precomputedResponse={precomputedResponse}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductionInsightsPage;
